/**
 * Broker List Tab Component
 * 
 * Displays a list of all broker platforms with filtering and management capabilities.
 * 
 * Features:
 * - View all brokers in a table/card format
 * - Filter by country
 * - Toggle active/inactive brokers
 * - Edit and deactivate broker actions
 * - Responsive design matching AddEditTradeAccount style
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import { Loader } from '../../../../components/ds';
import { services } from '../../../../services/api';
import { useSnackbar } from '../../../../contexts/SnackbarContext';
import { FiEdit, FiTrash2, FiSearch, FiGlobe, FiCheckCircle, FiXCircle, FiPower, FiRefreshCw } from 'react-icons/fi';
import ConfirmationModal from '../components/ConfirmationModal';
import Flag from 'react-world-flags';

const BrokerListTab = ({ onEditBroker }) => {
  const { showSnackbar } = useSnackbar();
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredBrokers, setFilteredBrokers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [countries, setCountries] = useState([]);

  // Modal State
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: 'warning',
    title: '',
    message: '',
    subMessage: '',
    action: null, // 'deactivate' | 'delete' | 'activate'
    broker: null
  });
  const [actionLoading, setActionLoading] = useState(false);

  // Load brokers on component mount
  useEffect(() => {
    loadBrokers();
  }, [showInactive]);

  /**
   * Load all brokers from API
   */
  const loadBrokers = async () => {
    try {
      setLoading(true);
      const response = await services.brokerPlatforms.getAllBrokers({ 
        include_inactive: showInactive 
      });
      
      // API returns nested structure: { data: { data: [...], status: "success" } }
      const brokerData = response.data?.data || [];
      setBrokers(brokerData);
      
      // Extract unique countries
      const uniqueCountries = [...new Set(brokerData.map(b => b.country_code))].sort();
      setCountries(uniqueCountries);
      
    } catch (error) {
      console.error('Error loading brokers:', error);
      showSnackbar('Failed to load brokers', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Filter brokers based on search and country selection
   */
  useEffect(() => {
    let filtered = brokers;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(broker =>
        broker.broker_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        broker.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        broker.country_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by country
    if (selectedCountry !== 'all') {
      filtered = filtered.filter(broker => broker.country_code === selectedCountry);
    }

    setFilteredBrokers(filtered);
  }, [brokers, searchTerm, selectedCountry]);

  /**
   * Initiate Action (Deactivate / Delete / Activate)
   */
  const initiateAction = (action, broker) => {
    let modalConfig = {
      isOpen: true,
      action: action,
      broker: broker,
      type: 'warning'
    };

    switch (action) {
      case 'deactivate':
        modalConfig.title = 'Deactivate Broker?';
        modalConfig.message = `Are you sure you want to deactivate ${broker.display_name}?`;
        modalConfig.subMessage = 'This will prevent users from connecting to this broker. Existing connections may be affected.';
        modalConfig.confirmText = 'Deactivate';
        break;
      case 'delete':
        modalConfig.title = 'Delete Broker Context?';
        modalConfig.message = `Are you sure you want to permanently delete settings for ${broker.display_name}?`;
        modalConfig.subMessage = 'WARNING: This action cannot be undone. All associated data and configurations will be permanently removed.';
        modalConfig.type = 'danger';
        modalConfig.confirmText = 'Delete Permanently';
        break;
      case 'activate':
        modalConfig.title = 'Activate Broker?';
        modalConfig.message = `Are you sure you want to activate ${broker.display_name}?`;
        modalConfig.subMessage = 'Users will be able to connect to this broker immediately.';
        modalConfig.type = 'info';
        modalConfig.confirmText = 'Activate';
        break;
      default:
        return;
    }

    setConfirmModal(modalConfig);
  };

  /**
   * Handle Confirm Action
   */
  const handleConfirmAction = async () => {
    if (!confirmModal.broker) return;
    
    setActionLoading(true);
    try {
      const { action, broker } = confirmModal;

      if (action === 'deactivate') {
        // Soft Deactivate using update
        await services.brokerPlatforms.updateBroker(broker.broker_id, { is_active: false });
        showSnackbar('Broker deactivated successfully', 'success');
      } else if (action === 'activate') {
        // Activate using update
        await services.brokerPlatforms.updateBroker(broker.broker_id, { is_active: true });
        showSnackbar('Broker activated successfully', 'success');
      } else if (action === 'delete') {
        // Hard Delete
        await services.brokerPlatforms.deleteBroker(broker.broker_id);
        showSnackbar('Broker deleted successfully', 'success');
      }

      // Close modal and reload
      setConfirmModal(prev => ({ ...prev, isOpen: false }));
      loadBrokers();

    } catch (error) {
      console.error(`Error performing ${confirmModal.action}:`, error);
      showSnackbar(`Failed to ${confirmModal.action} broker`, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Group and Sort Brokers
  const groupedBrokers = filteredBrokers.reduce((acc, broker) => {
    const country = broker.country_name || 'Other';
    if (!acc[country]) acc[country] = [];
    acc[country].push(broker);
    return acc;
  }, {});

  const sortedCountries = Object.keys(groupedBrokers).sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters Section */}
      <div className="bg-bg-3/50 rounded-xl p-4 border border-[var(--divider)] shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fg-3" />
            <input
              type="text"
              placeholder="Search brokers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ds-input w-full pl-10 transition-all"
            />
          </div>

          {/* Country Filter */}
          <div className="relative">
            <FiGlobe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fg-3" />
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="ds-input w-full pl-10 appearance-none transition-all"
            >
              <option value="all">All Countries</option>
              {countries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>

          {/* Show Inactive Toggle */}
          {/* py mirrors the DS coarse-pointer gate (<768px) so this matches the ds-input height in both */}
          <div className="flex items-center space-x-3 bg-bg-2 px-4 py-3 md:py-1.5 rounded-lg border border-[var(--divider)]">
            <input
              type="checkbox"
              id="showInactive"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="w-4 h-4 text-accent bg-bg-3 border-[var(--divider)] rounded focus:ring-accent focus:ring-2 cursor-pointer"
            />
            <label htmlFor="showInactive" className="text-sm font-medium text-fg-2 cursor-pointer select-none">
              Show Inactive Brokers
            </label>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between px-2">
        <p className="text-sm font-medium text-fg-2">
          Found <span className="text-fg-1">{filteredBrokers.length}</span> results
        </p>
        <button 
          onClick={loadBrokers}
          className="p-2 text-fg-3 hover:text-accent transition-colors"
          title="Refresh List"
        >
          <FiRefreshCw />
        </button>
      </div>

      {/* Brokers Grid */}
      {filteredBrokers.length === 0 ? (
        <div className="text-center py-16 bg-bg-3/30 rounded-xl border-2 border-dashed border-[var(--divider)]">
          <FiSearch className="mx-auto text-5xl text-fg-3 mb-4" />
          <h3 className="ds-h3 text-fg-1">No brokers found</h3>
          <p className="text-fg-2">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="space-y-12">
          {sortedCountries.map(country => {
            // Sort Brokers within Country Alphabetically
            const countryBrokers = groupedBrokers[country].sort((a, b) => 
              a.display_name.localeCompare(b.display_name)
            );
            const countryCode = countryBrokers[0]?.country_code;

            return (
              <div key={country}>
                {/* Country Header */}
                <div className="flex items-center gap-3 mb-6 pb-2 border-b border-[var(--divider)]">
                  {countryCode && (
                    <Flag 
                      code={countryCode} 
                      className="w-8 h-6 shadow-sm rounded-sm object-cover"
                      title={country}
                    />
                  )}
                  <h2 className="ds-h2 text-fg-1">
                    {country}
                  </h2>
                  <span className="text-sm font-medium text-fg-2 bg-bg-3 px-2.5 py-0.5 rounded-full">
                    {countryBrokers.length}
                  </span>
                </div>

                {/* Brokers Grid for Country */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                  {countryBrokers.map((broker) => (
                    <div
                      key={broker.broker_id}
                      className={`
                        group relative bg-bg-2 rounded-xl p-5 
                        border transition-all duration-300 hover:-translate-y-1 hover:shadow-lg
                        ${broker.is_active
                          ? 'border-[var(--divider)] hover:border-accent'
                          : 'border-[var(--divider)] bg-bg-3/50 opacity-90'
                        }
                      `}
                    >
                      {/* Card Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <Flag 
                              code={broker.country_code} 
                              className="w-6 h-4 shadow-sm rounded-sm object-cover" 
                              title={broker.country_name}
                            />
                            <h3 className="ds-h3 text-fg-1 truncate" title={broker.display_name}>
                              {broker.display_name}
                            </h3>
                          </div>
                          <p className="text-xs font-mono text-fg-3 uppercase tracking-wide">
                            {broker.broker_name}
                          </p>
                        </div>
                        <div className="flex-shrink-0 ml-4">
                            {broker.is_active ? (
                              <span className="ds-badge profit sm">
                                <FiCheckCircle /> Active
                              </span>
                            ) : (
                              <span className="ds-badge neutral sm">
                                <FiXCircle /> Inactive
                              </span>
                            )}
                        </div>
                      </div>

                      {/* Card Details */}
                      <div className="space-y-3 mb-6">
                        <div className="flex items-center text-sm p-2 rounded-lg bg-bg-3">
                          <FiGlobe className="mr-3 text-accent" />
                          <span className="text-fg-2">
                            {broker.country_name} <span className="text-fg-3 text-xs ml-1">({broker.country_code})</span>
                          </span>
                        </div>
                      </div>

                      {/* Actions Footer */}
                      <div className="flex items-center gap-2 pt-4 border-t border-[var(--divider)]">
                        <button
                          type="button"
                          onClick={() => onEditBroker(broker)}
                          className="btn btn-secondary btn-sm flex-1"
                        >
                          <FiEdit />
                          Edit
                        </button>

                        {broker.is_active ? (
                          <button
                            type="button"
                            onClick={() => initiateAction('deactivate', broker)}
                            className="btn btn-secondary btn-sm flex-1"
                            title="Soft Deactivate (Disable)"
                          >
                            <FiPower />
                            Disable
                          </button>
                        ) : (
                           <button
                            type="button"
                            onClick={() => initiateAction('activate', broker)}
                            className="btn btn-secondary btn-sm flex-1"
                          >
                            <FiCheckCircle />
                            Enable
                          </button>
                        )}

                        {/* Hard Delete Button */}
                        <button
                          onClick={() => initiateAction('delete', broker)}
                          className="flex-shrink-0 flex items-center justify-center px-3 h-10 md:h-8 bg-[var(--badge-loss-bg)] text-[var(--badge-loss-fg)] hover:bg-loss/20 rounded-lg text-sm transition-colors"
                          title="Permanently Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reusable Confirmation Modal */}
      <ConfirmationModal 
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={handleConfirmAction}
        title={confirmModal.title}
        message={confirmModal.message}
        subMessage={confirmModal.subMessage}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        isLoading={actionLoading}
      />
    </div>
  );
};

export default BrokerListTab;
